import { useMemo, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { dateKey, formatAgendaLabel, sortDayEvents } from '../../utils/dateUtils';

const CLOSE_DRAG_THRESHOLD = 90;
// From 4 events onward, cap the list's height so it scrolls internally
// instead of growing the sheet toward full-screen (spec: 0件=compact,
// 1〜3件=fits content, 4件以上=internal scroll, never full screen).
const SCROLL_FROM_COUNT = 4;
const SCROLL_BODY_MAX_HEIGHT = 340;

/**
 * "選択した日の予定" as a fully modal bottom sheet.
 *
 * While open, this is treated as a modal: EVERYTHING except an event
 * card closes it — the calendar, the today's-schedule area, blank
 * space, the bottom nav, the dimmed overlay, the ✕ button, and a
 * downward swipe. There is no more "tap another date to swap content
 * in place" behavior; changing date while the sheet is open now
 * always takes two taps (tap closes, tap again opens the new date).
 *
 * This is implemented with a single full-screen, pointer-events:auto
 * scrim that sits above the calendar/today's-schedule/top bar/bottom
 * nav (all inside the app's default stacking level) but below both
 * the day-sheet itself and the FAB (which stays usable, per the
 * existing "FAB is always available" behavior). Clicking the scrim —
 * which is what actually happens when tapping the calendar, nav, etc.
 * while the sheet is open, since the scrim physically covers them —
 * closes the sheet without ever reaching the element underneath, so
 * a "closing tap" never also re-selects a different day.
 *
 * Closing NEVER touches selectedDate, so the calendar's highlighted
 * day is preserved even after the sheet closes.
 *
 * Event cards live inside the sheet itself, above the scrim, so they
 * remain fully interactive and are unaffected by any of this.
 */
export default function SelectedDayBottomSheet() {
  const { selectedDate, events, dayEventsSheetOpen, closeDayEventsSheet, openEventDetail } = useApp();

  const dayEvents = useMemo(() => {
    const key = dateKey(selectedDate);
    return sortDayEvents(events.filter((e) => e.date === key));
  }, [events, selectedDate]);

  const dragStartY = useRef(null);
  const [dragY, setDragY] = useState(0);

  const handleDragStart = (e) => {
    dragStartY.current = (e.touches ? e.touches[0] : e).clientY;
  };
  const handleDragMove = (e) => {
    if (dragStartY.current == null) return;
    const clientY = (e.touches ? e.touches[0] : e).clientY;
    const delta = clientY - dragStartY.current;
    if (delta > 0) setDragY(delta);
  };
  const handleDragEnd = () => {
    if (dragStartY.current == null) return;
    if (dragY > CLOSE_DRAG_THRESHOLD) {
      closeDayEventsSheet();
    }
    dragStartY.current = null;
    setDragY(0);
  };

  // カードをタップすると予定詳細画面へ遷移する。選択日シートは閉じて
  // 詳細シートに置き換える（二重にシートが重ならないようにするため）。
  const handleCardClick = (ev) => {
    closeDayEventsSheet();
    openEventDetail(ev);
  };

  const bodyStyle =
    dayEvents.length >= SCROLL_FROM_COUNT ? { maxHeight: SCROLL_BODY_MAX_HEIGHT } : undefined;

  return (
    <>
      <div
        className={`day-sheet-scrim${dayEventsSheetOpen ? ' show' : ''}`}
        onClick={closeDayEventsSheet}
      />
      <div
        className={`day-sheet${dayEventsSheetOpen ? ' show' : ''}`}
        style={
          dragY
            ? { transform: `translateY(${dragY}px)`, transition: 'none' }
            : undefined
        }
      >
        <div
          className="day-sheet-drag-zone"
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
          onMouseDown={handleDragStart}
          onMouseMove={(e) => e.buttons === 1 && handleDragMove(e)}
          onMouseUp={handleDragEnd}
          onMouseLeave={() => dragStartY.current != null && handleDragEnd()}
        >
          <div className="day-sheet-handle" />
          <div className="day-sheet-head">
            <h2>{formatAgendaLabel(selectedDate)}</h2>
            <button className="close-btn" onClick={closeDayEventsSheet} type="button">
              ✕
            </button>
          </div>
        </div>

        <div className="day-sheet-body" style={bodyStyle} onClick={closeDayEventsSheet}>
          <div className="day-sheet-content" key={dateKey(selectedDate)}>
            {dayEvents.length === 0 ? (
              <div className="today-empty">予定はありません😊</div>
            ) : (
              <div className="day-agenda">
                {dayEvents.map((ev) => (
                  <div
                    className="agenda-item"
                    key={ev.id}
                    onClick={(e) => {
                      // 予定カードだけは例外 — バブリングを止めて
                      // day-sheet-body側の「閉じる」を発火させない。
                      e.stopPropagation();
                      handleCardClick(ev);
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="bar" style={{ '--cat-color': ev.color }} />
                    <div className="icon-dot" style={{ '--cat-color': ev.color }}>
                      {ev.icon}
                    </div>
                    <div className="body">
                      <div className="time-row">
                        {ev.allDay ? (
                          <span className="allday-chip">終日</span>
                        ) : (
                          `${ev.start}〜${ev.end}`
                        )}
                      </div>
                      <div className="title">{ev.title}</div>
                      {ev.location && <div className="loc">📍 {ev.location}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

